import React from 'react';
import DataToggle from '../DataToggle/DataToggle';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';

interface ToggleOption {
  code: string;
  label: string;
}

interface Column {
  label: string;
  column: string;
  type: 'static' | 'dynamic';
  toggle?: ToggleOption[];
  description?: string;
  sortable?: boolean;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
  renderCell?: (value: any, row: any, column: Column) => React.ReactNode;
  pinned?: 'left' | 'right' | false;
}

interface DataTableHeaderProps {
  column: Column;
  isLastColumn: boolean;
  currentToggleIndex: number;
  onToggleChange: (toggleIndex: number) => void;
  sortDirection?: 'asc' | 'desc' | null;
  onSort?: () => void;
  index: number;
  className?: string;
  pinned?: 'left' | 'right' | false;
}

/**
 * DataTableHeader - A table header component with sorting and toggle functionality
 *
 * @description A table header cell that supports sorting, dynamic toggles, column pinning, and full accessibility compliance.
 */
const DataTableHeader: React.FC<DataTableHeaderProps> = ({
  column,
  currentToggleIndex,
  onToggleChange,
  sortDirection = null,
  onSort,
  index,
  className,
  pinned,
}) => {
  const handleSortClick = () => {
    if (column.sortable && onSort) {
      onSort();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (column.sortable && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      handleSortClick();
    }
  };

  // Determine alignment
  const alignment = column.align || 'left';
  const textAlignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  }[alignment];

  // Add pinned-specific styling
  const pinnedClasses =
    pinned === 'left'
      ? 'shadow-[2px_0_4px_rgba(0,0,0,0.1)]'
      : pinned === 'right'
      ? 'shadow-[-2px_0_4px_rgba(0,0,0,0.1)]'
      : '';

  // Convert sort direction to ARIA-compatible values
  const ariaSort =
    column.sortable && sortDirection
      ? sortDirection === 'asc'
        ? 'ascending'
        : 'descending'
      : undefined;

  return (
    <div
      className={`px-3 py-3 text-xs font-medium text-primary 
          bg-white border-b border-neutral-300 sticky top-0 z-10 group
          hover:bg-neutral-100 cursor-pointer
           ${textAlignClass} ${pinnedClasses} ${className || ''}`}
      role="columnheader"
      aria-sort={ariaSort}
      aria-colindex={index + 1}
      onClick={handleSortClick}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-center h-full w-full">
        <div
          className={`flex flex-col gap-2 h-full w-full ${
            column.toggle ? 'justify-between' : 'justify-end'
          }`}
        >
          <div className="flex items-center w-full justify-between">
            <div className="text-xs text-primary leading-tight">
              {column.label}
              {column.description && (
                <span className="sr-only"> - {column.description}</span>
              )}
            </div>
            {column.sortable && (
              <button
                type="button"
                className={`flex items-center justify-center h-full w-5 cursor-pointer hover:text-blue-600 transition-all duration-200 ${
                  sortDirection
                    ? 'opacity-100'
                    : 'opacity-0 group-hover:opacity-100 focus:opacity-100'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSortClick();
                }}
                onKeyDown={handleKeyDown}
                aria-label={`Sort by ${column.label} ${
                  sortDirection === 'asc'
                    ? 'descending'
                    : sortDirection === 'desc'
                    ? 'ascending'
                    : 'ascending'
                }`}
                title={`Sort by ${column.label} ${
                  sortDirection === 'asc'
                    ? 'descending'
                    : sortDirection === 'desc'
                    ? 'ascending'
                    : 'ascending'
                }`}
              >
                <Icon
                  icon={faChevronDown}
                  className={`w-3 h-3 transition-all duration-300 ${
                    sortDirection === 'asc'
                      ? '-rotate-180'
                      : sortDirection === 'desc'
                      ? 'rotate-0'
                      : 'opacity-50'
                  }`}
                  aria-hidden="true"
                />
              </button>
            )}
          </div>
          {column.toggle && (
            <DataToggle
              options={column.toggle}
              currentIndex={currentToggleIndex}
              onChange={onToggleChange}
              columnLabel={column.label}
              size="sm"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default DataTableHeader;
